import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Card, Form, Button, Col } from 'react-bootstrap';
import moment from 'moment';
import { FaImage, FaPhotoVideo, FaFileAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';

import Dropzone from '../../components/Dropzone';
import Loading from '../../components/Loading';
import axios from '../../services/axios';
import ErrorProcessor from '../../components/ErrorProcessor';
import { useNotification } from '../../components/Notification/NotificationContext';
import DataTable from '../../components/DataTable';
import ButtonModal from '../../components/ModalConfirmation/ButtonModal';
import CardHeaderMore from '../../components/Cards/CardHeaderMore';

export default function LeadFile({ lead, stateAction, stateInfo }) {
  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState([]);
  const [fileName, setFileName] = useState('');
  const notification = useNotification();
  const [clearDropzone, setClearDropzone] = useState(false);
  const [apiErrors, setApiErrors] = useState({});
  const [leadData, setLeadData] = useState(lead);

  const tableColumnsFiles = useMemo(
    () => [
      {
        Header: 'Data',
        accessor: 'created_at',
      },
      {
        Header: 'Nome',
        accessor: 'name',
      },
      {
        Header: 'Arquivo',
        accessor: 'urlFile',
      },
      {
        Header: 'Tipo',
        accessor: 'type',
      },
    ],
    []
  );

  const getLead = useCallback(async () => {
    try {
      const request = await axios.get(`leads/${leadData.id}`);
      setLeadData({
        ...request.data,
      });
    } catch (err) {
      setApiErrors(err);
    } finally {
      stateAction(false);
    }
  }, [leadData]);

  const handleFileChange = (e) => {
    const newFile = e[0];
    setFile(newFile);
  };

  const handleSubmitFile = async (e) => {
    e.preventDefault();

    if (!file) {
      notification('Nenhum arquivo informado');
      return;
    }

    if (!fileName) {
      notification('Nome do arquivo não informado');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('lead_id', leadData.id);
    formData.append('name', fileName);

    try {
      setIsLoading(true);
      await axios.post(`/leadsfiles`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      notification('Arquivo carregado com sucesso', 'success');
      setClearDropzone(true);
      setFileName('');
      stateAction(true);
    } catch (err) {
      setApiErrors(err);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmDeleteFile = useCallback(async (idFile) => {
    try {
      setIsLoading(true);
      await axios.delete(`leadsfiles/${idFile}`);
      notification(`Arquivo removido com sucesso`, 'success');
      stateAction(true);
    } catch (err) {
      setApiErrors(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const tableDataFiles = useMemo(() => {
    return !leadData.LeadFiles
      ? []
      : leadData.LeadFiles.map((dataObject) => {
          let icon = null;
          let typeString = '';
          switch (dataObject.type) {
            case 'documents':
              icon = <FaFileAlt />;
              typeString = `Documento ${dataObject.file.split('.').pop()}`;
              break;
            case 'images':
              icon = <FaImage />;
              typeString = `Imagem ${dataObject.file.split('.').pop()}`;
              break;
            case 'medias':
              icon = <FaPhotoVideo />;
              typeString = `Áudio / vídeo ${dataObject.file.split('.').pop()}`;
              break;
            default:
              icon = null;
              break;
          }

          const linksAction = [
            {
              custom: (
                <ButtonModal
                  onConfirm={() => confirmDeleteFile(dataObject.id)}
                  message={`Confirma remoção do arquivo ${dataObject.name}?`}
                  buttonProps={{ text: 'Remover', faicon: 'faTrash' }}
                  textModal={{
                    title: `Remover arquivo`,
                    textConfirm: 'Remover',
                  }}
                  dropdown
                />
              ),
            },
          ];

          return {
            ...dataObject,
            created_at: moment.utc(dataObject.created_at).format('DD/MM/YYYY'),
            urlFile: (
              <Link
                to={dataObject.urlFile}
                title="Clique para abrir"
                target="_blank"
              >
                {icon}
              </Link>
            ),
            type: (
              <div className="d-flex justify-content-between">
                <span>{typeString}</span>{' '}
                <CardHeaderMore links={linksAction} className="" />
              </div>
            ),
          };
        });
  }, [leadData]);

  useEffect(() => {
    async function updateLead() {
      try {
        await getLead();
      } catch (error) {
        console.error(error);
      }
    }

    if (stateInfo === true) {
      updateLead();
    }
  }, [stateInfo]);

  return (
    <>
      <Col lg={4}>
        <Card className="mb-5">
          <ErrorProcessor error={apiErrors} />
          <Loading isLoading={isLoading} />
          <Card.Header>
            <h4 className="card-heading">Carregar arquivo</h4>
          </Card.Header>
          <Card.Body>
            <Form onSubmit={handleSubmitFile}>
              <div className="mb-2">
                <Form.Label className="form-label">Nome do arquivo</Form.Label>
                <Form.Control
                  type="text"
                  value={fileName}
                  onChange={(e) => setFileName(e.target.value)}
                />
              </div>
              <Dropzone
                onChange={handleFileChange}
                clear={clearDropzone}
                acceptMedia
                acceptDocument
              />
            </Form>
          </Card.Body>
          <Card.Footer>
            <Button
              variant="primary"
              type="button"
              onClick={handleSubmitFile}
              className="mt-2"
            >
              Carregar
            </Button>
          </Card.Footer>
        </Card>
      </Col>
      <Col lg={8}>
        <Card className="mb-5">
          <Card.Header>
            <h4 className="card-heading">Arquivos do lead</h4>
          </Card.Header>
          <Card.Body>
            <DataTable
              items={tableDataFiles}
              columns={tableColumnsFiles}
              defaultPageSize={5}
              defaultSort="created_at"
            />
          </Card.Body>
        </Card>
      </Col>
    </>
  );
}

LeadFile.propTypes = {
  lead: PropTypes.shape({}).isRequired,
  stateInfo: PropTypes.bool.isRequired,
  stateAction: PropTypes.func.isRequired,
};
